import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { signupUser } from '../../features/auth/authSlice';
import { RootState } from '../../store/store';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import AuthErrorMessage from './AuthErrorMessage';
import LoadingOverlay from './LoadingOverlay';

const SignupForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { loading, error: authError } = useSelector((state: RootState) => state.auth);
  const { showToast } = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const { values, errors, handleChange, handleSubmit, handleBlur, isValid } = useFormValidation(
    {
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      farmName: '',
    },
    {
      email: (value) => {
        if (!value.trim()) return 'Email is required';
        if (!/\S+@\S+\.\S+/.test(value)) return 'Invalid email format';
        return '';
      },
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';
      },
      confirmPassword: (value, allValues) => {
        if (!value) return 'Please confirm your password';
        if (value !== allValues.password) return 'Passwords do not match';
        return '';
      },
      fullName: (value) => !value.trim() ? 'Full name is required' : '',
      farmName: (value) => !value.trim() ? 'Farm name is required' : '',
    },
    async (formValues) => {
      try {
        const result = await dispatch(signupUser({
          email: formValues.email,
          password: formValues.password,
          metadata: {
            full_name: formValues.fullName,
            farm_name: formValues.farmName,
          }
        })).unwrap();
        
        if (result.user) {
          showToast('Account created successfully!', 'success');
          navigate('/dashboard');
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Signup failed', 'error');
      }
    }
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full space-y-8 relative">
        <LoadingOverlay show={loading} message="Creating your account..." />
        
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={values.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                  ${errors.fullName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
                  'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
              />
              {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
            </div>

            <div>
              <label htmlFor="farmName" className="block text-sm font-medium text-gray-700">
                Farm Name *
              </label>
              <input
                id="farmName"
                name="farmName"
                type="text"
                required
                value={values.farmName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                  ${errors.farmName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
                  'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
              />
              {errors.farmName && <p className="mt-1 text-sm text-red-600">{errors.farmName}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                  ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
                  'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                  ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
                  'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm
                  ${errors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
                  'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          {authError && <AuthErrorMessage error={authError} />}

          <div>
            <Button
              type="submit"
              disabled={!isValid || loading}
              className="w-full"
            >
              {loading ? 'Creating Account...' : 'Sign up'}
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/" className="font-medium text-green-600 hover:text-green-500">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SignupForm;